import { useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { useQuery, useQueryClient } from 'react-query'
import { AppLayoutContext } from '../../../providers/AppLayout'
import { useAppNavigate } from '../../../utils/url'
import { SnackbarContext } from '../../../providers/SnackbarContext'

import { AppPermissions } from '../../../models/AppPermissions.model'
import { WeatherRecord } from '../../../models/WeatherRecord.model'
import { WeatherRecordTypes } from '../../../models/WeatherRecordTypes.model'

import UserService from '../../../services/UserService/UserService'
import { WeatherRecordService } from '../../../services/WeatherRecordService/WeatherRecordService'

import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import Loading from '../../../components/Loading/Loading.component'
import Breadcrumbs from '../../../components/Breadcrumbs/Breadcrumbs.component'
import { formatDate } from '../../../utils/formatting'
import { weatherConditions } from '../../../models/WeatherConditions.model'
import FormDialog from '../../../components/FormDialog/FormDialog.component'
import WeatherRecordForm from '../shared/WeatherRecordForm.component'
import { AppStateContext } from '../../../providers/AppStateContext'
import DetailSection from '../../../components/Sections/DetailSection.component'
import DetailHeader from '../../../components/Sections/DetailHeader.component'
import { useEditSectionParam } from '../../../components/Sections/useEditSectionParam'
import { DetailField, DetailFieldGrid } from '../../../components/Sections/DetailField.component'
import { SectionDefinition } from '../../../components/Sections/Sections.types'
import { WeatherRecordSectionIds, weatherRecordSections } from '../shared/WeatherRecordSections'

// labels match the options in WeatherRecordForm
const weatherRecordTypeLabels: Record<WeatherRecordTypes, string> = {
  [WeatherRecordTypes.Algo]: 'Algorithm',
  [WeatherRecordTypes.Display]: 'Display',
}

// shows a reading with its unit, or '-' when the reading is missing (0 is a valid reading)
const withUnit = (value: number | null | undefined, unit: string) =>
  value === null || value === undefined ? '-' : `${value}${unit}`

const WeatherRecordDetail = (): JSX.Element => {
  const navigate = useAppNavigate()
  const { id } = useParams<{ id: string }>()
  const [loadingError, setLoadingError] = useState('')

  const { user } = useAuth0()
  const hasWritePermissions = UserService.hasPermissions(user, [AppPermissions.editWeatherRecord])

  const [isSaving, setIsSaving] = useState(false)
  const { isEditing, editSection, openEditor, closeEditor } = useEditSectionParam()
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false)

  const weatherRecordService = useMemo(() => new WeatherRecordService(), []) // memo to prevent re-creating service on every render

  useEffect(() => {
    AppLayoutContext.setPageName(`Weather Record Detail`)
  }, [id])

  const queryClient = useQueryClient()
  const { isLoading, data: weatherRecord } = useQuery(
    [
      'WeatherRecordService.get',
      {
        id: Number(id),
      },
      AppStateContext.getRegion(),
    ],
    () => weatherRecordService.get(Number(id)),
    {
      onError: (err) => {
        setLoadingError(typeof err === 'string' ? err : 'Failed to load weather record.')
        console.error(err)
      },
    },
  )

  const handleEdit = () => {
    closeEditor()
  }

  const handleDeleteClick = () => {
    // show a confirmation dialog before deleting
    setIsDeleteConfirmationOpen(true)
  }

  const handleDelete = () => {
    if (weatherRecord?.id) {
      setIsSaving(true)
      setIsDeleteConfirmationOpen(false)
      weatherRecordService
        .delete(weatherRecord.id)
        .then(() => {
          SnackbarContext.show(`Weather Record deleted successfully. You will now be redirected.`)

          // after a delay of 1 second, redirect to the weatherRecord list page
          setTimeout(() => {
            navigate(
              weatherRecord.weatherGroup ? `/weather-groups/${weatherRecord.weatherGroup.id}` : '/weather-records',
            )
            queryClient.removeQueries([
              'WeatherRecordService.get',
              {
                id: Number(id),
              },
            ])
            // invalidate all queries to ensure any queries that are using the modified record are updated
            queryClient.invalidateQueries()
          }, 1000)
        })
        .catch((err) => {
          console.error(err)
          SnackbarContext.show(`Weather Record failed to delete: ${err}`, 'error')
          setIsSaving(false)
        })
    }
  }

  const fullWeatherCondition = weatherConditions.find((condition) => condition.id === weatherRecord?.conditionId)

  const sectionById = (sectionId: WeatherRecordSectionIds) =>
    weatherRecordSections.find((section) => section.id === sectionId) as SectionDefinition
  // only offer per-section edit shortcuts to users who can edit
  const editSectionHandler = (sectionId: WeatherRecordSectionIds) =>
    hasWritePermissions ? () => openEditor(sectionId) : undefined

  return (
    <>
      {weatherRecord && (
        <FormDialog<WeatherRecord>
          open={isEditing}
          entity={weatherRecord}
          entityLabel="Weather Record"
          onClose={closeEditor}
        >
          <WeatherRecordForm
            weatherRecord={weatherRecord}
            initialSection={editSection}
            onEdit={handleEdit}
            onCancel={closeEditor}
          />
        </FormDialog>
      )}
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isDeleteConfirmationOpen}
        onClose={() => setIsDeleteConfirmationOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>Are you sure you want to delete this weather record?</DialogContent>
        <DialogActions>
          <Button autoFocus onClick={() => setIsDeleteConfirmationOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete}>Confirm</Button>
        </DialogActions>
      </Dialog>
      <Paper sx={{ p: 2 }}>
        {isLoading && !loadingError && <Loading sx={{ py: 20 }} />}
        {loadingError && (
          <Stack direction="row" justifyContent="center" alignItems="center" sx={{ py: 20 }}>
            <Typography variant="error">{loadingError}</Typography>
          </Stack>
        )}
        {!isLoading && !loadingError && weatherRecord && (
          <>
            {isSaving && <Loading sx={{ py: 20 }} />}
            {!isSaving && (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                  <Breadcrumbs
                    items={[
                      {
                        label: weatherRecord.weatherGroup ? weatherRecord.weatherGroup.name : 'Weather Records',
                        href: weatherRecord.weatherGroup
                          ? `/weather-groups/${weatherRecord.weatherGroup.id}`
                          : '/weather-records',
                      },
                      { label: weatherRecord.id.toString() },
                    ]}
                  />
                  {hasWritePermissions && (
                    <Stack direction="row" justifyContent="end" alignItems="center" spacing={2}>
                      <Button variant="text" onClick={handleDeleteClick}>
                        Delete
                      </Button>
                      <Button variant="contained" onClick={() => openEditor()}>
                        Edit
                      </Button>
                    </Stack>
                  )}
                </Stack>

                <DetailHeader
                  title={
                    weatherRecord.timestamp ? formatDate(weatherRecord.timestamp) : `Weather Record ${weatherRecord.id}`
                  }
                  badges={
                    weatherRecord.type && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={weatherRecordTypeLabels[weatherRecord.type] ?? weatherRecord.type}
                      />
                    )
                  }
                  subtitle={
                    <>
                      {weatherRecord.weatherGroup && (
                        <>
                          <Link href={`/weather-groups/${weatherRecord.weatherGroup.id}`}>
                            {weatherRecord.weatherGroup.name}
                          </Link>{' '}
                          ·{' '}
                        </>
                      )}
                      {weatherRecord.region} · ID {weatherRecord.id}
                    </>
                  }
                  meta={[
                    `Updated ${formatDate(weatherRecord.updatedAt)}`,
                    `Created ${formatDate(weatherRecord.createdAt)}`,
                  ]}
                />

                <Grid container spacing={2}>
                  {/* --- General ------------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(WeatherRecordSectionIds.general)}
                      onEdit={editSectionHandler(WeatherRecordSectionIds.general)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Weather Group">
                          {weatherRecord.weatherGroup ? (
                            <Link href={`/weather-groups/${weatherRecord.weatherGroup.id}`}>
                              {weatherRecord.weatherGroup.name}
                            </Link>
                          ) : (
                            '-'
                          )}
                        </DetailField>
                        <DetailField label="Type">
                          {weatherRecord.type ? weatherRecordTypeLabels[weatherRecord.type] ?? weatherRecord.type : '-'}
                        </DetailField>
                        <DetailField label="Timestamp">
                          {weatherRecord.timestamp ? formatDate(weatherRecord.timestamp) : '-'}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Temperature --------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(WeatherRecordSectionIds.temperature)}
                      onEdit={editSectionHandler(WeatherRecordSectionIds.temperature)}
                    >
                      <DetailFieldGrid minColumnWidth={120}>
                        <DetailField label="Temperature">{withUnit(weatherRecord.temperature, ' °C')}</DetailField>
                        <DetailField label="Feels Like">{withUnit(weatherRecord.feelsLike, ' °C')}</DetailField>
                        <DetailField label="Minimum">{withUnit(weatherRecord.tempMin, ' °C')}</DetailField>
                        <DetailField label="Maximum">{withUnit(weatherRecord.tempMax, ' °C')}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Precipitation ------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(WeatherRecordSectionIds.precipitation)}
                      onEdit={editSectionHandler(WeatherRecordSectionIds.precipitation)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Precipitation (Last 1 Hour)">
                          {withUnit(weatherRecord.precipLast1hr, ' mm')}
                        </DetailField>
                        <DetailField label="Snow (Last 1 Hour)">
                          {withUnit(weatherRecord.snowLast1hr, ' mm')}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Wind & Air ---------------------------------------------- */}
                  <Grid item zero={12} md={6}>
                    <DetailSection
                      section={sectionById(WeatherRecordSectionIds.wind)}
                      onEdit={editSectionHandler(WeatherRecordSectionIds.wind)}
                    >
                      <DetailFieldGrid minColumnWidth={120}>
                        <DetailField label="Wind Speed">{withUnit(weatherRecord.windSpeed, ' m/s')}</DetailField>
                        <DetailField label="Wind Direction">{withUnit(weatherRecord.windDirection, '°')}</DetailField>
                        <DetailField label="Pressure">{withUnit(weatherRecord.pressure, ' hPa')}</DetailField>
                        <DetailField label="Humidity">{withUnit(weatherRecord.relativeHumidity, '%')}</DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>

                  {/* --- Sky & Sun ----------------------------------------------- */}
                  <Grid item zero={12}>
                    <DetailSection
                      section={sectionById(WeatherRecordSectionIds.sky)}
                      onEdit={editSectionHandler(WeatherRecordSectionIds.sky)}
                    >
                      <DetailFieldGrid>
                        <DetailField label="Weather Condition">
                          {fullWeatherCondition
                            ? `${fullWeatherCondition.name}: ${fullWeatherCondition.description}`
                            : '-'}
                        </DetailField>
                        <DetailField label="UV Rating">{withUnit(weatherRecord.uv, '')}</DetailField>
                        {/* brightness is hidden, not removed: only Tempest stations supply it and
                            nothing consumes it, but the column and historical data are kept so it
                            can be re-surfaced without a migration */}
                        <DetailField label="Sunrise">
                          {weatherRecord.sunrise ? formatDate(weatherRecord.sunrise) : '-'}
                        </DetailField>
                        <DetailField label="Sunset">
                          {weatherRecord.sunset ? formatDate(weatherRecord.sunset) : '-'}
                        </DetailField>
                      </DetailFieldGrid>
                    </DetailSection>
                  </Grid>
                </Grid>
              </>
            )}
          </>
        )}
      </Paper>
    </>
  )
}
export default WeatherRecordDetail
